import XMarkIcon from '@heroicons/react/24/outline/XMarkIcon';
//@ts-ignore: Missing types in `beautiful-react-hooks`
import useDebouncedCallback from 'beautiful-react-hooks/useDebouncedCallback';
import { useCallback, useId, useMemo, useState } from 'react';

import { Button } from '@/components/core/Button';
import { ButtonPanel } from '@/components/core/ButtonPanel';
import { Checkbox } from '@/components/core/Checkbox';
import { Loader } from '@/components/core/Loader';
import { SearchInput } from '@/components/core/SearchInput/SearchInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/core/Select';
import { useValidationTranslator } from '@/components/datamodel/hooks/useValidationTranslator';
import { useTranslation } from '@/hooks/useTranslation';
import type { AssetFilterGroup } from '@/pages/dam/api';
import { useItemSearch } from '@/pages/dam/api/linkedItems.queries';
import { type DamUploadFile, MIXED_VALUE, sharedValue, unionItems } from '@/pages/dam/utils/damUploadFiles';
import { cn } from '@/services/utils';
import type { PersistedItem } from '@/types/DataModel';

const SEARCH_DEBOUNCE_MS = 300;
const NOT_SPECIFIED = '__not_specified__';
const MIXED = '__mixed__';

type DamProductTarget = 'all' | 'none';

interface DamUploadDrawerProps {
    mode: 'link' | 'data';
    files: DamUploadFile[];
    /** Fields of `asset_upload` with their options (data mode). */
    fields: AssetFilterGroup[];
    /** Names of mandatory fields, marked with "*". */
    mandatory: string[];
    describeItem: (item: PersistedItem) => string;
    /** Products to add to / remove from all edited files (link mode). */
    onConfirmLinks: (added: PersistedItem[], removed: string[]) => void;
    /** Values to set on all edited files; fields left "mixed" are not included (data mode). */
    onConfirmValues: (values: Record<string, unknown>) => void;
    onClose: () => void;
}

/**
 * Side panel "Editing N files" of the upload wizard (wireframe `7330:245818;7523:346505`,
 * `7328:237429;7510:267458`). Link mode: product search and the products linked to the files; a
 * product linked to only some files shows a dash and is linked to all on click. Data mode: one
 * select per `asset_upload` field, "Mixed specifications" while the files differ. Changes apply on
 * Confirm.
 */
function DamUploadDrawer({ mode, files, fields, mandatory, describeItem, onConfirmLinks, onConfirmValues, onClose }: DamUploadDrawerProps) {
    const { t } = useTranslation();
    const titleId = useId();

    return (
        <aside
            aria-labelledby={titleId}
            className={cn(
                'tw-absolute tw-bottom-0 tw-right-0 tw-top-0 tw-z-10 tw-flex tw-w-full tw-flex-col tw-gap-4 tw-overflow-y-auto md:tw-w-[30rem]',
                'tw-border-y-0 tw-border-l tw-border-r-0 tw-border-solid tw-border-primary tw-bg-fill-primary tw-p-5 tw-shadow-xl',
            )}
        >
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                <h3 id={titleId} className="tw-m-0 tw-text-t-base tw-font-bold tw-text-t-primary lg:tw-text-t-lg">
                    {files.length === 1 ? t('DAM.UPLOAD.EDITING_ONE') : t('DAM.UPLOAD.EDITING_MANY', { count: files.length.toLocaleString() })}
                </h3>
                <ButtonPanel isDisabled={false} icon={XMarkIcon} tooltip={t('CLOSE')} aria-label={t('CLOSE')} onClick={onClose} />
            </div>
            {mode === 'link'
                ? <LinkEditor files={files} describeItem={describeItem} onConfirm={onConfirmLinks} onCancel={onClose} />
                : <DataEditor files={files} fields={fields} mandatory={mandatory} onConfirm={onConfirmValues} onCancel={onClose} />}
        </aside>
    );
}

interface LinkEditorProps {
    files: DamUploadFile[];
    describeItem: (item: PersistedItem) => string;
    onConfirm: (added: PersistedItem[], removed: string[]) => void;
    onCancel: () => void;
}

const LinkEditor = ({ files, describeItem, onConfirm, onCancel }: LinkEditorProps) => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [keyword, setKeyword] = useState('');
    const [found, setFound] = useState<PersistedItem[]>([]);
    const [targets, setTargets] = useState<Record<string, DamProductTarget>>({});
    const commitKeyword = useCallback((value: string) => setKeyword(value.trim()), []);
    const applyKeyword = useDebouncedCallback(commitKeyword, [commitKeyword], SEARCH_DEBOUNCE_MS);
    const { data: results = [], isFetching } = useItemSearch({ variables: { keyword }, enabled: keyword.length > 0 });

    const linked = useMemo(() => unionItems(files), [files]);
    const shown = useMemo(
        () => [...linked, ...found.filter((item) => !linked.some((existing) => existing.primaryKey__ === item.primaryKey__))],
        [linked, found],
    );
    const countOf = (key: string) => files.filter((file) => file.items.some((item) => item.primaryKey__ === key)).length;
    const stateOf = (key: string) => {
        const target = targets[key];
        const count = target === 'all' ? files.length : target === 'none' ? 0 : countOf(key);
        return { isAll: count === files.length, isSome: count > 0 && count < files.length };
    };
    const toggle = (key: string) => setTargets((current) => ({ ...current, [key]: stateOf(key).isAll ? 'none' : 'all' }));
    const add = (item: PersistedItem) => {
        setFound((current) => (current.some((existing) => existing.primaryKey__ === item.primaryKey__) ? current : [...current, item]));
        setTargets((current) => ({ ...current, [item.primaryKey__]: 'all' }));
    };

    const changed = Object.entries(targets).filter(([key, target]) => (target === 'all' ? countOf(key) < files.length : countOf(key) > 0));
    const added = shown.filter((item) => changed.some(([key, target]) => key === item.primaryKey__ && target === 'all'));
    const removed = changed.filter(([, target]) => target === 'none').map(([key]) => key);

    return (
        <>
            <SearchInput
                defaultOpen
                aria-label={t('DAM.UPLOAD.SEARCH_PRODUCTS')}
                placeholder={t('DAM.UPLOAD.SEARCH_PRODUCTS')}
                value={searchTerm}
                onChange={(value: string) => {
                    setSearchTerm(value);
                    applyKeyword(value);
                }}
            />
            {keyword && (
                isFetching ? (
                    <div className="tw-flex tw-justify-center tw-py-2"><Loader /></div>
                ) : results.length === 0 ? (
                    <p className="tw-m-0 tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">{t('DAM.DETAIL.NO_ARTICLES_FOUND')}</p>
                ) : (
                    <ul className="tw-m-0 tw-flex tw-max-h-60 tw-list-none tw-flex-col tw-overflow-y-auto tw-rounded tw-border tw-border-solid tw-border-tertiary tw-p-0">
                        {results.map((item) => (
                            <li key={item.primaryKey__}>
                                <Button variant="ghost" onClick={() => add(item)} className="tw-h-auto tw-w-full tw-justify-start tw-px-3 tw-py-2 tw-text-left tw-font-normal">
                                    {describeItem(item)}
                                </Button>
                            </li>
                        ))}
                    </ul>
                )
            )}
            <div className="tw-flex tw-flex-col tw-gap-2">
                <span className="tw-text-t-sm tw-font-bold tw-text-t-primary lg:tw-text-t-base">{t('DAM.UPLOAD.LINKED_PRODUCTS')}</span>
                {shown.length === 0 ? (
                    <p className="tw-m-0 tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">{t('DAM.UPLOAD.NO_PRODUCTS_LINKED')}</p>
                ) : (
                    <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
                        {shown.map((item) => {
                            const { isAll, isSome } = stateOf(item.primaryKey__);
                            return (
                                <li key={item.primaryKey__}>
                                    <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-3 tw-text-t-sm tw-text-t-primary lg:tw-text-t-base">
                                        <Checkbox isChecked={isAll} isIndeterminate={isSome} onCheckedChange={() => toggle(item.primaryKey__)} />
                                        {describeItem(item)}
                                    </label>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
            <div className="tw-flex tw-gap-2">
                <Button disabled={changed.length === 0} onClick={() => onConfirm(added, removed)}>{t('DAM.TAGS.CONFIRM')}</Button>
                <Button variant="outline" onClick={onCancel}>{t('CANCEL')}</Button>
            </div>
        </>
    );
};

interface DataEditorProps {
    files: DamUploadFile[];
    fields: AssetFilterGroup[];
    mandatory: string[];
    onConfirm: (values: Record<string, unknown>) => void;
    onCancel: () => void;
}

const DataEditor = ({ files, fields, mandatory, onConfirm, onCancel }: DataEditorProps) => {
    const { t } = useTranslation();
    const { translateValidationMessage } = useValidationTranslator();
    const [draft, setDraft] = useState<Record<string, string>>({});

    const initialOf = (name: string) => {
        const value = sharedValue(files, name);
        if (value === MIXED_VALUE) {
            return MIXED;
        }
        return typeof value === 'string' && value !== '' ? value : NOT_SPECIFIED;
    };

    const changedNames = Object.keys(draft).filter((name) => draft[name] !== initialOf(name));

    /** One file: its messages for the field; several files: how many of them have errors there (D-051). */
    const messagesOf = (name: string): { text: string; isError: boolean }[] => {
        if (files.length === 1) {
            const { errors = {}, warnings = {} } = files[0].validation ?? {};
            return [
                ...(errors[name] ?? []).map((entry) => ({ text: translateValidationMessage(entry, name), isError: true })),
                ...(warnings[name] ?? []).map((entry) => ({ text: translateValidationMessage(entry, name), isError: false })),
            ];
        }
        const count = files.filter((file) => (file.validation?.errors[name] ?? []).length > 0).length;
        return count > 0 ? [{ text: t('DAM.UPLOAD.SELECTION_ERRORS', { count: count.toLocaleString() }), isError: true }] : [];
    };

    return (
        <>
            {fields.map((field) => {
                const value = draft[field.attributeName] ?? initialOf(field.attributeName);
                return (
                    <label key={field.attributeName} className="tw-flex tw-flex-col tw-gap-1 tw-text-t-sm tw-text-t-primary lg:tw-text-t-base">
                        <span>
                            {field.label}
                            {mandatory.includes(field.attributeName) && <span className="tw-text-error" aria-hidden> *</span>}
                        </span>
                        <Select value={value} onValueChange={(next) => setDraft((current) => ({ ...current, [field.attributeName]: next }))}>
                            <SelectTrigger aria-label={field.label} aria-required={mandatory.includes(field.attributeName)}>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {value === MIXED && <SelectItem value={MIXED} disabled>{t('DAM.UPLOAD.MIXED')}</SelectItem>}
                                <SelectItem value={NOT_SPECIFIED}>{t('DAM.UPLOAD.NOT_SPECIFIED')}</SelectItem>
                                {field.options.map((option) => <SelectItem key={option.key} value={option.key}>{option.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        {messagesOf(field.attributeName).map((message) => (
                            <span key={message.text} className={cn('tw-text-t-xs lg:tw-text-t-sm', message.isError ? 'tw-text-error-dark' : 'tw-text-warning-dark')}>
                                {message.text}
                            </span>
                        ))}
                    </label>
                );
            })}
            <div className="tw-flex tw-gap-2">
                <Button
                    disabled={changedNames.length === 0}
                    onClick={() => onConfirm(Object.fromEntries(changedNames.map((name) => [name, draft[name] === NOT_SPECIFIED ? null : draft[name]])))}
                >
                    {t('DAM.TAGS.CONFIRM')}
                </Button>
                <Button variant="outline" onClick={onCancel}>{t('CANCEL')}</Button>
            </div>
        </>
    );
};

export { DamUploadDrawer };
export type { DamUploadDrawerProps };
